import { DOCUMENT, SINGLETON } from './schemas';

/**
 * Document Types that should be shown in Internal Link fields, e.g. in Header Nav Menu.
 */
export const INTERNAL_LINK_TYPES = [
  { type: DOCUMENT.PAGES },
  { type: DOCUMENT.BUSINESS_HOMEPAGE },
  { type: DOCUMENT.LEGAL_PAGES },
  { type: DOCUMENT.CAREER_PAGES },
  { type: DOCUMENT.ABOUT_FOOD_WASTE_PAGES },
  { type: DOCUMENT.ABOUT_FOOD_WASTE_BLOG },
  { type: DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES },
  { type: DOCUMENT.ABOUT_US_PAGES },
  { type: DOCUMENT.BUSINESS_SOLUTIONS_SOLUTIONS },
  { type: DOCUMENT.BUSINESS_SOLUTIONS_MARKETPLACES },
  { type: DOCUMENT.MARKETPLACE_PAGES },
  { type: DOCUMENT.PARCELS_PAGES },
  { type: DOCUMENT.PLATFORM_PAGES },
  { type: DOCUMENT.DATE_LABELLING_PAGES },
  { type: DOCUMENT.DONATIONS_PAGES },
  { type: DOCUMENT.PRESS_RELEASES },
  { type: DOCUMENT.INITIATIVE_PAGES },
  { type: DOCUMENT.ACQUISITION_B2B_PAGES },
  { type: DOCUMENT.INITIATIVE_B2B_PAGES },
  { type: DOCUMENT.ACQUISITION_B2C_PAGES },
  { type: DOCUMENT.SURPRISE_BAG_HOME },
  { type: DOCUMENT.BRAND_PROFILE },
];

/** Page types owned by the business site (apps/business sitemap). */
const BUSINESS_SITEMAP_DOCUMENT_TYPES = new Set<string>([
  DOCUMENT.BUSINESS_HOMEPAGE,
  DOCUMENT.BUSINESS_SOLUTIONS_SOLUTIONS,
  DOCUMENT.BUSINESS_SOLUTIONS_MARKETPLACES,
  DOCUMENT.MARKETPLACE_PAGES,
  DOCUMENT.PARCELS_PAGES,
  DOCUMENT.PLATFORM_PAGES,
  DOCUMENT.DATE_LABELLING_PAGES,
  DOCUMENT.DONATIONS_PAGES,
  DOCUMENT.ACQUISITION_B2B_PAGES,
  DOCUMENT.INITIATIVE_B2B_PAGES,
]);

/**
 * Document types which:
 *  - should not be able to be created in the 'new document' menu
 *  - should not be able to be duplicated, unpublished or deleted
 *
 * This is commonly used for singletons.
 */
export const LOCKED_DOCUMENT_TYPES: Array<string> = [
  ...Object.values(SINGLETON), // All Singleton types
  DOCUMENT.MEDIA_TAG,
  DOCUMENT.AUDIENCE,
];

/**
 * Document types that are able to display a live preview pane.
 */
export const PREVIEWABLE_DOCUMENT_TYPES: Array<string> = [
  SINGLETON.THEME,
  DOCUMENT.NAVIGATION_HEADERS,
  DOCUMENT.NAVIGATION_FOOTERS,
  DOCUMENT.PAGES,
  DOCUMENT.BUSINESS_HOMEPAGE,
  DOCUMENT.LEGAL_PAGES,
  DOCUMENT.CAREER_PAGES,
  DOCUMENT.SURPRISE_BAG_HOME,
  DOCUMENT.ABOUT_FOOD_WASTE_PAGES,
  DOCUMENT.ABOUT_FOOD_WASTE_BLOG,
  DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES,
  DOCUMENT.ABOUT_US_PAGES,
  DOCUMENT.BUSINESS_SOLUTIONS_SOLUTIONS,
  DOCUMENT.BUSINESS_SOLUTIONS_MARKETPLACES,
  DOCUMENT.MARKETPLACE_PAGES,
  DOCUMENT.PARCELS_PAGES,
  DOCUMENT.PLATFORM_PAGES,
  DOCUMENT.DATE_LABELLING_PAGES,
  DOCUMENT.DONATIONS_PAGES,
  DOCUMENT.PRESS_RELEASES,
  DOCUMENT.INITIATIVE_PAGES,
  DOCUMENT.ACQUISITION_B2B_PAGES,
  DOCUMENT.INITIATIVE_B2B_PAGES,
  DOCUMENT.ACQUISITION_B2C_PAGES,
];

/**
 * Document types that can display a live preview pane, but require a slug to do so.
 * NOTE, these types must also be included in "PREVIEWABLE_DOCUMENT_TYPES".
 */
export const PREVIEWABLE_DOCUMENT_TYPES_REQUIRING_SLUGS: typeof PREVIEWABLE_DOCUMENT_TYPES = [
  DOCUMENT.PAGES,
  DOCUMENT.BUSINESS_HOMEPAGE,
  DOCUMENT.LEGAL_PAGES,
  DOCUMENT.CAREER_PAGES,
  DOCUMENT.SURPRISE_BAG_HOME,
  DOCUMENT.ABOUT_FOOD_WASTE_PAGES,
  DOCUMENT.ABOUT_FOOD_WASTE_BLOG,
  DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES,
  DOCUMENT.ABOUT_US_PAGES,
  DOCUMENT.BUSINESS_SOLUTIONS_SOLUTIONS,
  DOCUMENT.BUSINESS_SOLUTIONS_MARKETPLACES,
  DOCUMENT.MARKETPLACE_PAGES,
  DOCUMENT.PARCELS_PAGES,
  DOCUMENT.PLATFORM_PAGES,
  DOCUMENT.DATE_LABELLING_PAGES,
  DOCUMENT.DONATIONS_PAGES,
  DOCUMENT.PRESS_RELEASES,
  DOCUMENT.INITIATIVE_PAGES,
  DOCUMENT.ACQUISITION_B2B_PAGES,
  DOCUMENT.INITIATIVE_B2B_PAGES,
  DOCUMENT.ACQUISITION_B2C_PAGES,
];

/**
 * Document types that are able to display an SEO Preview pane.
 */
export const SEO_PREVIEW_DOCUMENT_TYPES: Array<string> = [
  ...PREVIEWABLE_DOCUMENT_TYPES.filter((type) => type !== SINGLETON.THEME), // All previewable types except THEME
  SINGLETON.CONFIG_SEO,
];

/**
 * Document types that should have the "SEO" analysis pane.
 */
export const SEO_ANALYSIS_DOCUMENT_TYPES: Array<string> = [
  DOCUMENT.PAGES,
  DOCUMENT.BUSINESS_HOMEPAGE,
  DOCUMENT.LEGAL_PAGES,
  DOCUMENT.CAREER_PAGES,
  DOCUMENT.ABOUT_FOOD_WASTE_PAGES,
  DOCUMENT.SURPRISE_BAG_PAGES,
  DOCUMENT.ABOUT_FOOD_WASTE_BLOG,
  DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES,
  DOCUMENT.ABOUT_US_PAGES,
  DOCUMENT.BUSINESS_SOLUTIONS_SOLUTIONS,
  DOCUMENT.BUSINESS_SOLUTIONS_MARKETPLACES,
  DOCUMENT.MARKETPLACE_PAGES,
  DOCUMENT.PARCELS_PAGES,
  DOCUMENT.PLATFORM_PAGES,
  DOCUMENT.DATE_LABELLING_PAGES,
  DOCUMENT.DONATIONS_PAGES,
  DOCUMENT.PRESS_RELEASES,
  DOCUMENT.INITIATIVE_PAGES,
  DOCUMENT.ACQUISITION_B2B_PAGES,
  DOCUMENT.INITIATIVE_B2B_PAGES,
  DOCUMENT.ACQUISITION_B2C_PAGES,
];

/**
 * Document types that should have the "incoming references" pane when editing.
 * This pane lists all other documents that reference this document.
 * Useful for viewing a quick list of pages that use a particular form, for instance.
 */
export const INCOMING_REFERENCE_LIST: Array<string> = [
  ...INTERNAL_LINK_TYPES.map((docType) => docType['type']),
  DOCUMENT.FRAGMENT_FORMS,
  DOCUMENT.FRAGMENT_SALESFORCE_FORMS,
  DOCUMENT.FRAGMENT_SUPPORT_PATH,
  DOCUMENT.FRAGMENT_SIGNATURE_GENERATOR,
  DOCUMENT.FAQ,
  DOCUMENT.FRAGMENT_SURPRISE_BAG,
  DOCUMENT.FRAGMENT_REPORTS,
  DOCUMENT.FRAGMENT_BRAND_PROFILE_DEFAULTS,
  DOCUMENT.STATISTICS,
  DOCUMENT.PEOPLE,
  DOCUMENT.AUTHORS,
  DOCUMENT.ABOUT_FOOD_WASTE_BLOG_CATEGORIES,
  DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES_CATEGORIES,
  DOCUMENT.PRESS_MEDIA_ASSETS_CATEGORIES,
  DOCUMENT.AUDIENCE,
];

/**
 * Document types that have document-level translations. See: https://github.com/sanity-io/document-internationalization
 *
 * These schemas MUST implement the `LOCALISE_LANGUAGE_FIELD_NAME` field.
 * This should be done by adding the `defineLanguageField` utility in `./schemas/utils/localisation`. to the schema fields.
 * See: https://github.com/sanity-io/sanity-plugin-internationalized-array
 */
export const LOCALISE_DOCUMENT_LEVEL_DOCUMENT_TYPES: Array<string> = [
  DOCUMENT.PAGES,
  DOCUMENT.BUSINESS_HOMEPAGE,
  DOCUMENT.LEGAL_PAGES,
  DOCUMENT.CAREER_PAGES,
  DOCUMENT.ABOUT_FOOD_WASTE_PAGES,
  DOCUMENT.SURPRISE_BAG_PAGES,
  DOCUMENT.ABOUT_FOOD_WASTE_BLOG,
  DOCUMENT.ABOUT_FOOD_WASTE_BLOG_CATEGORIES,
  DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES,
  DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES_CATEGORIES,
  DOCUMENT.ABOUT_US_PAGES,
  DOCUMENT.BUSINESS_SOLUTIONS_SOLUTIONS,
  DOCUMENT.BUSINESS_SOLUTIONS_MARKETPLACES,
  DOCUMENT.MARKETPLACE_PAGES,
  DOCUMENT.PARCELS_PAGES,
  DOCUMENT.PLATFORM_PAGES,
  DOCUMENT.DATE_LABELLING_PAGES,
  DOCUMENT.DONATIONS_PAGES,
  DOCUMENT.MARKETPLACES_VENDOR_TYPE,
  SINGLETON.THEME,
  DOCUMENT.ANNOUNCEMENTS,
  DOCUMENT.NAVIGATION_HEADERS,
  DOCUMENT.NAVIGATION_FOOTERS,
  DOCUMENT.FRAGMENT_SALESFORCE_FORMS,
  DOCUMENT.FRAGMENT_FORMS,
  DOCUMENT.FRAGMENT_SUPPORT_PATH,
  DOCUMENT.FRAGMENT_SURPRISE_BAG,
  DOCUMENT.FRAGMENT_SIGNATURE_GENERATOR,
  DOCUMENT.FRAGMENT_BRAND_PROFILE_DEFAULTS,
  DOCUMENT.PRESS_RELEASES,
  DOCUMENT.PRESS_MEDIA_ASSETS,
  DOCUMENT.PRESS_MEDIA_ASSETS_CATEGORIES,
  DOCUMENT.PRESS_NEWS_COVERAGE,
  DOCUMENT.DEFAULT_BLOCKS,
  DOCUMENT.INITIATIVE_PAGES,
  DOCUMENT.ACQUISITION_B2B_PAGES,
  DOCUMENT.INITIATIVE_B2B_PAGES,
  DOCUMENT.ACQUISITION_B2C_PAGES,
  DOCUMENT.SURPRISE_BAG_HOME,
  DOCUMENT.BRAND_PROFILE,
];

/**
 * Document types that have field-level translations. See: https://github.com/sanity-io/sanity-plugin-internationalized-array
 * This will replace fields with the appropriate field-level translatable fields, as well as show the "Showing x/y translations"
 * dropdown in top-right of editor pane (allowing editors to show or hide certain languages).
 */
export const LOCALISE_FIELD_LEVEL_DOCUMENT_TYPES: Array<string> = [
  SINGLETON.CONFIG_SEO,
  DOCUMENT.FAQ,
  DOCUMENT.STATISTICS,
  DOCUMENT.PEOPLE,
  DOCUMENT.AUTHORS,
  DOCUMENT.PARTNER_LOGO,
  SINGLETON.CONFIG_APPSTORES,
];

/**
 * Document Types that have a hard-coded prefix.
 * Keys are the Document Type, and values are what the prefix should be, which must start with a `/`.
 *
 * Most document types will not need to be added here, as their page slug is fully defined in the
 * CMS, from their slug, and sometimes from their parent page's slug too.
 */
export const DOCUMENT_SLUG_PREFIX = {
  // 'blog': '/blog', // This is just an example, remove it if we add any
};

/**
 * Listing document types. This will be used to identify document types that consist of listing content.
 */

export const LISTING_DOCUMENT_TYPES = [
  { title: 'Blog', value: DOCUMENT.ABOUT_FOOD_WASTE_BLOG },
  { title: 'Resources', value: DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES },
  { title: 'Press Releases', value: DOCUMENT.PRESS_RELEASES },
  { title: 'News Coverage', value: DOCUMENT.PRESS_NEWS_COVERAGE },
  { title: 'Media Assets', value: DOCUMENT.PRESS_MEDIA_ASSETS },
]


/**
 * Document taxonomies - This will be used to link documents with a taxonomy.
 * Blog -> Blog Categories
 */

export const DOCUMENT_TAXONOMIES = [
  {
    document: DOCUMENT.ABOUT_FOOD_WASTE_BLOG,
    taxonomies: [{
        title: 'Blog Categories',
        value: DOCUMENT.ABOUT_FOOD_WASTE_BLOG_CATEGORIES
      }
    ]
  },
  {
    document: DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES,
    taxonomies: [{
      title: 'Resource Categories',
      value: DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES_CATEGORIES
    }
    ]
  },
  {
    document: DOCUMENT.PRESS_MEDIA_ASSETS,
    taxonomies: [{
      title: 'Press media assets Categories',
      value: DOCUMENT.PRESS_MEDIA_ASSETS_CATEGORIES
    }
    ]
  },
]

/**
 * Document Types that can be exported for Translation and re-imported with translations.
 *
 * This will usually put a "Translations" view on the document when viewing it.
 */
export const DOCUMENTS_EXPORT_TRANSLATION: Array<string> = [
  ...LOCALISE_DOCUMENT_LEVEL_DOCUMENT_TYPES.filter((type) => type !== SINGLETON.THEME), // All previewable types except THEME,
  // @todo Field-level types are not currently supported by import script
  // ...LOCALISE_FIELD_LEVEL_DOCUMENT_TYPES,
];

/**
 * All page-level document types, before they are split between the www and
 * business sitemaps.
 */
export const DOCUMENTS_IN_UNIFIED_SITEMAP: Array<string> = INTERNAL_LINK_TYPES
  .map(docType => docType.type);

/**
 * Page-level document types listed on the consumer (www) sitemap.
 * Business-only types are excluded — they belong on apps/business.
 */
export const DOCUMENTS_IN_SITEMAP: Array<string> = DOCUMENTS_IN_UNIFIED_SITEMAP
  .filter(typeId => !BUSINESS_SITEMAP_DOCUMENT_TYPES.has(typeId));

/**
 * Page-level document types listed on the business site sitemap.
 * Audience filtering is applied at query time.
 */
export const DOCUMENTS_IN_BUSINESS_SITEMAP: Array<string> = DOCUMENTS_IN_UNIFIED_SITEMAP
  .filter(typeId => BUSINESS_SITEMAP_DOCUMENT_TYPES.has(typeId));
